import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import PlaceIcon from "@mui/icons-material/Place";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import GroupIcon from "@mui/icons-material/Group";
import ArticleIcon from "@mui/icons-material/Article";
import { Link as RouterLink, useParams } from "react-router";
import { usePostcode } from "../hooks/useApiQueries.js";
import { ContentCard } from "../components/ContentCard.js";
import { SEO } from "../components/SEO.js";
import { NeonEyebrow, NeonDivider, neonButtonSx } from "../components/Neon.js";
import { avatarInitial } from "../lib/displayHelpers.js";

const SIBLING_POSTCODES = ["3525", "3527", "3517", "3556", "3559", "3575"];

export function BuiltNearYou() {
  const { postcode = "" } = useParams();
  const { data, isLoading } = usePostcode(postcode);
  if (isLoading)
    return (
      <>
        <Skeleton variant="rectangular" height={180} sx={{ borderRadius: 2, mb: 3 }} />
        <Skeleton variant="rectangular" height={400} />
      </>
    );
  if (!data) return <Alert severity="error">Postcode not found.</Alert>;

  const suburb = data.profile.suburb;
  const region = data.profile.region;
  const contentCount = data.content.length;
  const operatorCount = data.operators.length;

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Place",
      name: `${suburb}, Australia (${postcode})`,
      address: { "@type": "PostalAddress", postalCode: postcode, addressCountry: "AU" },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://corianpark.au/" },
        {
          "@type": "ListItem",
          position: 2,
          name: suburb,
          item: `https://corianpark.au/built-near-you/${postcode}`,
        },
      ],
    },
  ];

  return (
    <>
      <SEO
        title={`${suburb} earthmoving (${postcode})`}
        description={`Earthmoving content, projects, and operators near ${suburb}, ${region}. Get a quote for earthwork in ${postcode}.`}
        canonical={`https://corianpark.au/built-near-you/${postcode}`}
        jsonLd={jsonLd}
      />

      {/* Header band — bleed escape */}
      <Box
        sx={{
          mx: { xs: -2, sm: -3 },
          mt: -4,
          mb: { xs: 4, md: 6 },
          py: { xs: 5, md: 7 },
          px: { xs: 2, sm: 3, md: 4 },
          background:
            "linear-gradient(135deg, rgba(255,176,0,0.12) 0%, rgba(255,136,0,0.04) 60%, transparent 100%)",
          borderBottom: "1px solid",
          borderColor: "divider",
          position: "relative",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            left: 0,
            bottom: 0,
            width: "100%",
            height: 3,
            background: "linear-gradient(90deg, #ffb000 0%, #ff8800 50%, transparent 100%)",
            boxShadow: "0 0 12px rgba(255,176,0,0.7)",
          }}
        />
        <Box sx={{ maxWidth: 1200, mx: "auto" }}>
          <NeonEyebrow glow>BUILT NEAR YOU · {region.toUpperCase()}</NeonEyebrow>
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: 38, md: 64 },
              fontWeight: 900,
              letterSpacing: "-0.02em",
              mt: 1,
              mb: 1,
              lineHeight: 1.05,
            }}
          >
            {suburb}{" "}
            <Box component="span" sx={{ color: "primary.main", fontWeight: 800 }}>
              · {postcode}
            </Box>
          </Typography>
          <Stack direction="row" spacing={1.5} sx={{ mt: 2, flexWrap: "wrap", gap: 1 }}>
            <Chip
              icon={<ArticleIcon sx={{ fontSize: 16 }} />}
              label={`${contentCount} ${contentCount === 1 ? "article" : "articles"}`}
              sx={{ bgcolor: "background.paper", fontWeight: 700 }}
            />
            <Chip
              icon={<GroupIcon sx={{ fontSize: 16 }} />}
              label={`${operatorCount} ${operatorCount === 1 ? "operator" : "operators"}`}
              sx={{ bgcolor: "background.paper", fontWeight: 700 }}
            />
            <Chip
              icon={<PlaceIcon sx={{ fontSize: 16 }} />}
              label={region}
              sx={{ bgcolor: "background.paper" }}
            />
          </Stack>
        </Box>
      </Box>

      {/* Content (lead with this — content discovery is the platform DNA) */}
      <Box sx={{ mb: 5 }}>
        <NeonEyebrow glow>FROM YOUR PATCH</NeonEyebrow>
        <Typography
          variant="h3"
          sx={{ fontSize: { xs: 22, md: 28 }, fontWeight: 800, mt: 1, mb: 1 }}
          data-testid="built-near-content"
        >
          Filmed and shared near {suburb}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 560 }}>
          Videos and articles from operators working in your area.
        </Typography>
        {contentCount === 0 ? (
          <Alert severity="info">No content tagged here yet — we're expanding coverage.</Alert>
        ) : (
          <Grid container spacing={2}>
            {data.content.map((c) => (
              <Grid key={c.id} size={{ xs: 12, sm: 6, md: 4 }}>
                <ContentCard item={c} showSaveButton />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      <NeonDivider sx={{ my: 5 }} />

      {/* Operators — demoted, smaller, footer-style strip */}
      {operatorCount > 0 && (
        <Box sx={{ mb: 5 }}>
          <NeonEyebrow>OPERATORS NEAR YOU</NeonEyebrow>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 1, mb: 2 }}
            data-testid="built-near-operators"
          >
            On the network in {suburb} and nearby.
          </Typography>
          <Stack direction="row" sx={{ flexWrap: "wrap", gap: 1.5 }}>
            {data.operators.map((op) => (
              <Box
                key={op.id}
                component={RouterLink}
                to={`/operators/${op.slug}`}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  px: 1.5,
                  py: 1,
                  borderRadius: 999,
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: "background.paper",
                  color: "inherit",
                  textDecoration: "none",
                  transition: "all 200ms ease",
                  "&:hover": {
                    borderColor: "primary.main",
                    color: "primary.main",
                  },
                }}
              >
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    bgcolor: "primary.main",
                    color: "common.black",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 900,
                    fontSize: 13,
                    flexShrink: 0,
                  }}
                >
                  {avatarInitial(op.name)}
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{op.name}</Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      {/* Sibling postcodes */}
      <Box sx={{ mb: 5 }}>
        <NeonEyebrow>NEARBY</NeonEyebrow>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 1, mb: 1.5 }}
        >
          Browse other postcodes in the {region}:
        </Typography>
        <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", gap: 0.5 }}>
          {SIBLING_POSTCODES.filter((p) => p !== postcode).map((p) => (
            <Chip
              key={p}
              component={RouterLink}
              to={`/built-near-you/${p}`}
              icon={<PlaceIcon sx={{ fontSize: 14 }} />}
              label={p}
              size="small"
              clickable
              sx={{
                bgcolor: "background.paper",
                "&:hover": { borderColor: "primary.main" },
              }}
            />
          ))}
        </Stack>
      </Box>

      {/* CTA */}
      <Box
        sx={{
          p: { xs: 3, md: 4 },
          borderRadius: 1,
          background:
            "linear-gradient(135deg, rgba(255,176,0,0.14) 0%, rgba(255,136,0,0.04) 60%, transparent 100%)",
          border: "1px solid",
          borderColor: "primary.main",
          boxShadow: "0 0 30px -10px rgba(255,176,0,0.5)",
        }}
        data-testid="built-near-cta"
      >
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ alignItems: { sm: "center" } }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
              Need earthwork done near {suburb}?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              The Corian Park team routes quotes to operators with the right gear in your
              postcode — fast.
            </Typography>
          </Box>
          <Button
            component={RouterLink}
            to={`/quote?postcode=${postcode}`}
            variant="contained"
            color="primary"
            size="large"
            startIcon={<RequestQuoteIcon />}
            sx={{ ...neonButtonSx, flexShrink: 0 }}
          >
            Get a quote in {postcode}
          </Button>
        </Stack>
      </Box>
    </>
  );
}
